import React from 'react';
import PropTypes from 'prop-types';
import './KPICard.css';

/**
 * KPICard Component
 * Displays a key performance indicator with value, trend, and comparison
 */
const KPICard = ({
    title,
    value,
    previousValue,
    format = 'number',
    currency = 'EUR',
    icon,
    gradient = 'blue',
    loading = false,
    decimals = 0
}) => {
    // Calculate trend
    const calculateTrend = () => {
        if (!previousValue || previousValue === 0) return null;

        const change = ((value - previousValue) / previousValue) * 100;
        return {
            value: Math.abs(change),
            direction: change >= 0 ? 'up' : 'down',
            isPositive: change >= 0
        };
    };

    // Format value based on type
    const formatValue = (val) => {
        if (val === null || val === undefined) return '-';

        switch (format) {
            case 'currency':
                return new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: currency,
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals
                }).format(val);

            case 'percentage':
                return `${val.toFixed(decimals)}%`;

            case 'number':
            default:
                return new Intl.NumberFormat('fr-FR', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals
                }).format(val);
        }
    };

    const trend = calculateTrend();

    // Gradient classes
    const gradientClass = `kpi-card-gradient-${gradient}`;

    return (
        <div className={`kpi-card ${gradientClass}`}>
            <div className="kpi-card-content">
                <div className="kpi-card-header">
                    <div className="kpi-card-title">{title}</div>
                    {icon && (
                        <div className="kpi-card-icon">
                            <i className={icon}></i>
                        </div>
                    )}
                </div>

                <div className="kpi-card-body">
                    {loading ? (
                        <div className="kpi-card-skeleton">
                            <div className="skeleton-line skeleton-value"></div>
                            <div className="skeleton-line skeleton-trend"></div>
                        </div>
                    ) : (
                        <>
                            <div className="kpi-card-value">{formatValue(value)}</div>

                            {trend && (
                                <div className={`kpi-card-trend ${trend.isPositive ? 'positive' : 'negative'}`}>
                                    <i className={`bi bi-arrow-${trend.direction}`}></i>
                                    <span>{trend.value.toFixed(1)}%</span>
                                    <span className="kpi-card-trend-label">vs période précédente</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

KPICard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    previousValue: PropTypes.number,
    format: PropTypes.oneOf(['number', 'currency', 'percentage']),
    currency: PropTypes.string,
    icon: PropTypes.string,
    gradient: PropTypes.oneOf(['blue', 'green', 'orange', 'purple', 'red']),
    loading: PropTypes.bool,
    decimals: PropTypes.number
};

export default KPICard;
